import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Check, Loader2 } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { Seo } from "@/components/ui/Seo";
import { ErrorBanner } from "@/components/ui/States";
import { LinkedInButton } from "@/components/ui/LinkedInButton";
import { useFetch } from "@/hooks/useFetch";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaUrl, apiErrorMessage } from "@/lib/api";
import { localizedPath, pick } from "@/lib/helpers";

const INQUIRIES = ["general", "services", "partnership", "technical", "media"];

/**
 * Convert any map link into something embeddable inside an <iframe>.
 *
 * The Google Maps "share" / "place" URL (e.g. https://www.google.com/maps/place/...)
 * is NOT embed-friendly — Google sends X-Frame-Options: SAMEORIGIN so the iframe
 * shows a broken-icon placeholder. Two URL forms ARE embeddable without an API key:
 *   1. https://www.google.com/maps?q=<address>&output=embed
 *   2. The official /maps/embed/v1/place URL (requires an API key)
 *
 * Strategy:
 *  - If mapUrl already contains "/embed" or "output=embed" or is an OpenStreetMap
 *    embed, use it as-is.
 *  - Otherwise treat it as a "needs conversion" link and build a fallback embed
 *    from the address fields (which are guaranteed to be present in settings).
 *  - Return both the iframe src AND a human "open in maps" href.
 */
function resolveMap(settings) {
  const raw = (settings.mapUrl || "").trim();
  const address = [
    settings.addressEn,
    settings.city,
    settings.province,
    settings.postalCode,
    settings.country,
  ].filter(Boolean).join(", ");
  const q = encodeURIComponent(address || "Ottawa, Ontario, Canada");
  const fallbackEmbed = `https://www.google.com/maps?q=${q}&output=embed`;
  const humanHref = raw && /^https?:\/\//.test(raw)
    ? raw
    : `https://www.google.com/maps/search/?api=1&query=${q}`;

  // Detect embed-friendly URLs.
  const isEmbed =
    /(\/maps\/embed|output=embed|openstreetmap\.org\/export\/embed|maps-iframe)/i.test(raw);
  return { embedSrc: isEmbed ? raw : fallbackEmbed, humanHref };
}

export default function Contact() {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { data: page, error, refetch } = useFetch(`/pages/contact`, []);

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", organization: "", phone: "",
    inquiryType: "general", message: "", consent: true, company_website: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverError, setServerError] = useState("");

  // Never block the page on a single failed fetch; the form + address still work.

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.firstName.trim()) er.firstName = t("contact.required");
    if (!form.lastName.trim()) er.lastName = t("contact.required");
    if (!form.email.trim()) er.email = t("contact.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = t("contact.invalidEmail");
    if (!form.message.trim()) er.message = t("contact.required");
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ firstName: "", lastName: "", email: "", organization: "", phone: "", inquiryType: "general", message: "", consent: true, company_website: "" });
    } catch (err) {
      setStatus("error");
      setServerError(apiErrorMessage(err.response?.data?.detail, t("contact.error")));
    }
  };

  const addr = [
    pick({ en: settings.addressEn, fr: settings.addressFr }, lang),
    [settings.city, settings.province, settings.postalCode].filter(Boolean).join(", "),
    settings.country,
  ].filter(Boolean);

  return (
    <>
      <Seo
        title={pick(page?.seo?.metaTitle, lang) || t("nav.contact")}
        description={pick(page?.seo?.metaDescription, lang)}
        lang={lang}
        path={localizedPath(lang, "/contact")}
        image={mediaUrl(page?.heroImageUrl)}
      />

      <Hero
        eyebrow={pick(page?.heroEyebrow, lang)}
        title={pick(page?.heroTitle, lang) || t("nav.contact")}
        subtitle={pick(page?.heroSubtitle, lang)}
        image={page?.heroImageUrl}
      />

      {error && !page ? <ErrorBanner onRetry={refetch} testId="contact-content-error" /> : null}

      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-20">
          {/* Form */}
          <div>
            <h2 className="h-display text-fg text-3xl">{t("contact.formTitle")}</h2>
            <div className="mt-4 h-px w-16 bg-cyan" />

            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 glass-card p-8 text-center"
                  data-testid="contact-success"
                >
                  <div className="mx-auto w-12 h-12 grid place-items-center border border-cyan text-accent">
                    <Check className="w-6 h-6" />
                  </div>
                  <p className="mt-5 text-fg text-lg">{t("contact.success")}</p>
                  <button type="button" className="btn-secondary mt-6" onClick={() => setStatus("idle")} data-testid="contact-reset">
                    {t("cta.getInTouch")}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  className="mt-8 space-y-5"
                  noValidate
                  data-testid="contact-form"
                  initial={{ opacity: 1 }}
                >
                  {/* honeypot */}
                  <input
                    type="text"
                    name="company_website"
                    value={form.company_website}
                    onChange={set("company_website")}
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field id="firstName" label={t("contact.firstName")} value={form.firstName} onChange={set("firstName")} error={errors.firstName} required />
                    <Field id="lastName" label={t("contact.lastName")} value={form.lastName} onChange={set("lastName")} error={errors.lastName} required />
                  </div>
                  <Field id="email" type="email" label={t("contact.email")} value={form.email} onChange={set("email")} error={errors.email} required />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field id="organization" label={t("contact.organization")} value={form.organization} onChange={set("organization")} />
                    <Field id="phone" label={t("contact.phone")} value={form.phone} onChange={set("phone")} />
                  </div>

                  <div>
                    <label htmlFor="inquiryType" className="label-tech">{t("contact.inquiryType")}</label>
                    <select id="inquiryType" value={form.inquiryType} onChange={set("inquiryType")} className="input-tech" data-testid="contact-inquiry">
                      {INQUIRIES.map((q) => (
                        <option key={q} value={q} className="bg-surface">{t(`contact.inquiry.${q}`)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="label-tech">{t("contact.message")} *</label>
                    <textarea
                      id="message"
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      className="input-tech resize-y"
                      data-testid="contact-message"
                      aria-invalid={!!errors.message}
                    />
                    {errors.message ? <p className="mt-1 text-danger text-sm" data-testid="error-message">{errors.message}</p> : null}
                  </div>

                  <label className="flex items-start gap-3 text-sm text-mut cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                      className="mt-1 accent-[#00D4FF] w-4 h-4"
                      data-testid="contact-consent"
                    />
                    {t("contact.consent")}
                  </label>

                  {status === "error" ? (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-danger text-sm" data-testid="contact-error">
                      {serverError}
                    </motion.p>
                  ) : null}

                  <button type="submit" className="btn-primary" disabled={status === "sending"} data-testid="contact-submit">
                    {status === "sending" ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {t("contact.sending")}</>
                    ) : (
                      t("contact.submit")
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Info */}
          <aside className="space-y-8" data-testid="contact-info">
            <h2 className="h-display text-fg text-3xl">{t("contact.infoTitle")}</h2>
            <div className="space-y-6">
              {settings.contactEmail ? (
                <InfoRow icon={Mail} label={t("contact.emailLabel")}>
                  <a href={`mailto:${settings.contactEmail}`} className="text-fg hover:text-accent transition-colors" data-testid="contact-email-link">
                    {settings.contactEmail}
                  </a>
                </InfoRow>
              ) : null}
              {settings.phone ? (
                <InfoRow icon={Phone} label={t("contact.phoneLabel")}>
                  <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} className="text-fg hover:text-accent transition-colors" data-testid="contact-phone-link">
                    {settings.phone}
                  </a>
                </InfoRow>
              ) : null}
              {addr.length ? (
                <InfoRow icon={MapPin} label={t("contact.addressLabel")}>
                  <address className="not-italic text-fg leading-relaxed">
                    {addr.map((line, i) => <div key={`${line}-${i}`}>{line}</div>)}
                  </address>
                </InfoRow>
              ) : null}
              <div className="pt-2">
                <LinkedInButton url={settings.linkedinUrl} label={t("nav.linkedin")} />
              </div>
            </div>
            {(() => {
              const map = resolveMap(settings);
              return (
                <div className="border border-hair aspect-video overflow-hidden relative group" data-testid="contact-map">
                  <iframe
                    title="Map"
                    src={map.embedSrc}
                    className="w-full h-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                  <a
                    href={map.humanHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-map-open"
                    className="absolute bottom-3 right-3 bg-bg/85 backdrop-blur border border-hair px-3 py-1.5 text-xs font-techmono uppercase tracking-widest text-fg hover:text-accent transition-colors"
                  >
                    Open in Maps
                  </a>
                </div>
              );
            })()}
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ id, label, type = "text", value, onChange, error, required }) {
  return (
    <div>
      <label htmlFor={id} className="label-tech">{label}{required ? " *" : ""}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className="input-tech"
        aria-invalid={!!error}
        data-testid={`contact-${id}`}
      />
      {error ? <p className="mt-1 text-danger text-sm" data-testid={`error-${id}`}>{error}</p> : null}
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 shrink-0 grid place-items-center border border-hair text-accent">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="label-tech mb-1">{label}</div>
        {children}
      </div>
    </div>
  );
}
